import React from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { fetchClient } from 'common/actions/clientActions';
import { fetchExternalConfig } from 'common/actions/appActions';
import { getProviders } from 'common/actions/authenticationActions';
import { activate } from 'common/actions/activationActions';
import { setPageInfo } from 'page/actions';
import MessagingContainer from 'modal/messaging/MessagingContainer';
import openFeedbackPage from 'lib/ui/openFeedbackPage';
import { isIframeClosedSelector } from 'selectors/activationSelectors';
import { nrCookie } from 'constants';
import appConfig from '../../config';

const {
    shellVersion,
    secureCdnHost
} = appConfig;

const styles = {
    hide: {
        display: 'none'
    },
    errorTxt: {
        color: '#c00'
    },
    bold: {
        fontWeight: 'bold'
    }
};

const DEFAULT_VALUE = 'initialValue'; // empty value might hit the affilites w/o abbreviation in the console
const NOT_FOUND = 'notfound';
const noIds = [DEFAULT_VALUE, NOT_FOUND];

export class Activate extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            regCode: '',
            provider: DEFAULT_VALUE,
            errors: {
                regCodeError: false,
                providerError: false
            },
            isSubmitting: false,
            imgError: false
        };
        this.onProviderChange = this.onProviderChange.bind(this);
        this.onCodeChange = this.onCodeChange.bind(this);
        this.onCodeKeyUp = this.onCodeKeyUp.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onImgError = this.onImgError.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const { genericError, invalidError } = nextProps;
        const hasError = genericError || invalidError;
        const isIframeClosed = nextProps.isIframeClosed && !this.props.isIframeClosed;
        if (this.state.isSubmitting && (hasError || isIframeClosed)) {
            this.setState({
                isSubmitting: false
            });
        }
    }

    onProviderChange(event) {
        this.setState({
            provider: event.target.value
        }, () => this.state.provider && this.txtRegCode.focus());
    }

    onCodeChange(event) {
        this.setState({
            regCode: event.target.value.toUpperCase()
        });
    }

    onCodeKeyUp(event) {
        if (event.which === 13) {
            this.onSubmit();
        }
    }

    onSubmit() {
        if (this.state.isSubmitting) {
            return;
        }

        const errors = {
            regCodeError: !this.state.regCode,
            providerError: !this.isProviderValid
        };
        this.setState({ errors }, () => this.isValid && this.doAuth());
    }

    onImgError() {
        this.setState({
            imgError: true
        });
    }

    get isProviderValid() {
        return noIds.every(id => this.state.provider !== id);
    }

    get isValid() {
        return Object.keys(this.state.errors).every(key => !this.state.errors[key]);
    }

    doAuth() {
        this.setState({
            isSubmitting: true
        });

        this.props.dispatch(activate(this.state.regCode, this.state.provider));
    }

    renderActivate() {
        const {
            providers,
            genericError,
            invalidError
        } = this.props;
        const {
            provider,
            regCode,
            errors: { regCodeError, providerError }
        } = this.state;

        const hasRegCodeError = !regCode && regCodeError;

        // build provider list
        const listItem = providers.map(item =>
            (
                <option value={item.abbreviation}>{item.name}</option>
            )
        );
        listItem.unshift(<option value={DEFAULT_VALUE}>&nbsp;</option>);
        listItem.push(<option disabled>-----------------</option>);
        listItem.push(<option value="notfound">Not Found</option>);

        return (
            <div>
                <header className="section" id="page-title">
                    <h1>Activate Your Device</h1>
                </header>
                <section className="section" id="choose-provider">
                    <h2>Choose your provider</h2>
                    <div>
                        <select
                            id="selAffiliate"
                            className="temp"
                            value={provider}
                            onChange={this.onProviderChange}
                        >
                            {listItem}
                        </select>
                    </div>
                    {(providerError) ? (
                        <div id="no-provider">
                            <p style={styles.errorTxt}>Please choose your provider.</p>
                        </div>
                    ) : null}
                </section>
                <section className="section" id="enter-code">
                    <h2>Enter your activation code</h2>
                    <div>
                        <input
                            ref={input => { this.txtRegCode = input; }}
                            id="txtRegCode"
                            name="txtRegCode"
                            type="text"
                            maxLength="7"
                            value={this.state.regCode}
                            style={{ textTransform: 'uppercase' }}
                            onChange={this.onCodeChange}
                            onKeyUp={this.onCodeKeyUp}
                        />
                    </div>
                    {genericError ? (
                        <div id="generic-error">
                            <p style={styles.errorTxt}>An error occurred. Please try again later.</p>
                        </div>
                    ) : null}
                    {(!genericError && invalidError) ? (
                        <div id="invalid-activation-code">
                            <p style={styles.errorTxt}>Invalid activation code!</p>
                        </div>
                    ) : null}
                    {(!genericError && hasRegCodeError) ? (
                        <div id="no-activation-code">
                            <p style={styles.errorTxt}>Please enter an activation code.</p>
                        </div>
                    ) : null}
                </section>
                <section className="section" id="authenticate">
                    <h2>Verify your subscription</h2>
                    <p>{'Click continue to visit your provider\'s website and verify your account'}</p>
                    <div>
                        <input
                            id="cont-btn"
                            name="submit"
                            type="submit"
                            className={`btn-action ${(provider && regCode) ? '' : 'btn-action-disabled'}`}
                            onClick={this.onSubmit}
                            value="CONTINUE"
                        />
                    </div>
                </section>
            </div>
        );
    }

    renderSuccess(deviceType = 'xbox') {
        const { imgError } = this.state;
        const verifyScreenImageUrl = `${secureCdnHost}/prod/assets/watchespn/activate/` +
            `code-screen-${imgError ? 'appletv' : deviceType}.jpg`;

        return (
            <div>
                <header className="section" id="page-title">
                    <h1>Activation Successful</h1>
                </header>

                <section className="section success" id="enter-code">
                    <h2>Your device is now activated!</h2>
                    <p>You can now return to your device and enjoy ESPN programming</p>

                    <p>
                        <img
                            src={verifyScreenImageUrl}
                            onError={this.onImgError}
                            alt="Verify Screen"
                        />
                    </p>

                    <p>
                        If you experience any issues, please contact Customer Care&nbsp;
                        <a onClick={openFeedbackPage}>by Email</a>&nbsp;
                        or by calling 888-549-ESPN.
                    </p>
                </section>
            </div>
        );
    }

    render() {
        const { location: { query: { error, authSuccess, deviceType } = {} } = {} } = this.props;

        let content;
        if (error === 'true') {
            content = Activate.renderError();
        } else if (authSuccess === 'true') {
            content = this.renderSuccess(deviceType);
        } else {
            content = this.renderActivate();
        }

        return (
            <div>
                {/* #content.container width adjustment because of box-sizing from page-shell.css */}
                <Helmet
                    meta={[
                        { name: 'robots', content: 'noindex, nofollow' }
                    ]}
                    link={[
                        { id: 'font-link', rel: 'stylesheet', href: `${secureCdnHost}/redesign/fonts/base64-benton-woff.css` },
                        { rel: 'stylesheet', href: `${secureCdnHost}/redesign/${shellVersion}/css/shell-desktop.css` },
                        { rel: 'stylesheet', href: `${secureCdnHost}/redesign/${shellVersion}/css/page-shell.css` },
                        {
                            rel: 'stylesheet',
                            href: `${secureCdnHost}/combiner/c?v=2013005061650&css=` +
                            'espn3/global_header.r27.css,' +
                            'espn3/espn.core.201001071754.css,' +
                            'espn3/universal_overlay01.css,' +
                            'espn3/jquery.qtip.css,' +
                            'espn3/watchespn/styles.css,' +
                            'espn3/watchespn/xbox.css,' +
                            'espn3/watchespn/logout_index.css,' +
                            'espn3/watchespn/simulcastblackout.css,' +
                            'espn3/watchespn/simulcastblackout2.css,' +
                            'espn3/watchespn/activate/activate.css'
                        },
                        {
                            rel: 'stylesheet',
                            href: `${secureCdnHost}/combiner/c?css=` +
                            'fonts/klavika.css,' +
                            'fonts/klavikamedium.css,' +
                            'fonts/klavikabold.css,' +
                            'fonts/klavikaboldcondensed.css,' +
                            'fonts/klavikamediumcondensed.css,' +
                            'fonts/klavikacondensed.css,' +
                            'fonts/espnscore.css'
                        },
                        { rel: 'stylesheet', href: `${secureCdnHost}/redesign/${shellVersion}/css/watch.css` },
                        { rel: 'stylesheet', href: `${secureCdnHost}/redesign/${shellVersion}/css/watch-provider.css` }
                    ]}
                    style={[{
                        cssText: `
                            #content.container {
                                width: 400px;
                            }
                            @media screen and (max-width: 767px) {
                                #content.container {
                                    width: 290px;
                                }
                            }
                        `
                    }]}
                    script={[{
                        innerHTML: nrCookie
                    }]}
                />
                <MessagingContainer />
                <div id="content" className="container">
                    <div id="watch-logo">
                        <a href="http://espn.com/watch/">
                            WatchESPN
                        </a>
                    </div>
                    {content}
                </div>
                <div id="footer">
                    <span style={styles.bold}>
                        <a href="https://www.espn.com">ESPN.com</a>:
                    </span>
                    <a href="/watch/schedule">Schedule</a>&nbsp;|&nbsp;
                    <a href="https://www.espn.com/watchespn/faq/_/device/xbox" rel="nofollow">FAQs</a>&nbsp;|&nbsp;
                    <a onClick={openFeedbackPage}>Customer Care</a>
                </div>
            </div>
        );
    }
}

Activate.propTypes = {
    dispatch: React.PropTypes.func.isRequired,
    providers: React.PropTypes.array,
    genericError: React.PropTypes.string,
    invalidError: React.PropTypes.string,
    location: React.PropTypes.object,
    isIframeClosed: React.PropTypes.bool
};

Activate.defaultProps = {
    providers: []
};

Activate.renderError = () => (
    <div>
        <header className="section error" id="page-title">
            <h1>Activation Error</h1>
        </header>

        <section className="section error" id="enter-code">
            <h2>Your device could not be activated activated!</h2>
            <p>Please verify the code you entered matches the code on your device and try again.</p>
            <p>If you continue to receive this message please try generating a new code on the device.</p>
        </section>
    </div>
);

Activate.loadActions = [
    fetchExternalConfig,
    fetchClient,
    (_, query) => getProviders({ platform: query.device }),
    () => setPageInfo('activate', 'Device Activation')
];

const mapStateToProps = (state) => {
    const {
        providerDialog: { providers = [] } = {},
        activation: { genericError = {}, invalidError }
    } = state;
    const isIframeClosed = isIframeClosedSelector(state);

    return {
        providers,
        genericError,
        invalidError,
        isIframeClosed
    };
};

export default connect(mapStateToProps)(Activate);



// WEBPACK FOOTER //
// ./app/page/Activate.js